import { Button, ConfirmDialog, Field, Message, TextAreaInput } from 'Components/Core';
import { ListFieldSection } from 'Components/Layout';
import { ListDropdown } from 'Components/List';
import { actions } from 'Data/ReduxViewModels/InterLab';
import { Severity } from 'Data/ViewModels/Core/Severity';
import { isNil } from 'ramda';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ButtonGroup } from 'semantic-ui-react';
import { InterLabViewHistoryDialog } from '../Share';

const InterLabTransfer: React.FC = () => {
  const dispatch = useDispatch();
  const { laboratoryId, isClientGlobal, isProjectGlobal, laboratory, samples, id, containsUnsupportedSamples } =
    useSelector((state) => state.interLab.workOrder) || {};
  const busyState = useSelector((state) => state.interLab.status.busy) || false;

  const [laboratoryOption, setLaboratoryOption] = useState<any>(null);
  const [transferNote, setTransferNote] = useState<string | undefined>();

  const handleLaboratoryChange = ({ data: { value } }: any) => {
    setLaboratoryOption(isNil(value) ? null : { value: value.value, label: value.label });
  };

  const laboratorySelected = !!laboratoryOption;
  const transferNoteHasValue = !!transferNote && transferNote.trim().length > 0;
  const workOrderSelected = !!laboratoryId;
  const anySampleHasNonGlobalSpecification = samples?.some((s) => s.hasNonGlobalSpecification);

  const messages: { message: string; hasMessage?: boolean; severity?: Severity }[] = [
    {
      message: 'A destination laboratory must be selected to transfer the work order.',
      hasMessage: !laboratorySelected,
    },
    {
      message: 'Please enter a transfer note to transfer the work order.',
      hasMessage: !transferNoteHasValue,
    },
    {
      message:
        !isClientGlobal && !isProjectGlobal
          ? 'Clients and Projects must be global in scope in order to transfer work orders.'
          : !isClientGlobal
          ? 'Clients must be global in scope in order to transfer work orders.'
          : !isProjectGlobal
          ? 'Projects must be global in scope in order to transfer work orders.'
          : '',
      hasMessage: !isClientGlobal || !isProjectGlobal,
    },
    {
      message:
        'Work order contains invalid samples. Only Aggregate/Soil, Asphalt, and Bitumen samples can be transferred.',
      hasMessage: containsUnsupportedSamples,
      severity: 'error',
    },
  ];

  const transfer = () => {
    dispatch(
      actions.transfer({ workOrderId: id!, laboratoryId: laboratoryOption.value!, transferNote: transferNote! })
    );
  };

  if (laboratoryOption && laboratoryOption.value === laboratoryId) {
    setLaboratoryOption(null);
  }

  return (
    <>
      <ListFieldSection>
        <Field
          input={<ListDropdown />}
          value={laboratoryOption}
          label="Transfer To"
          endpoint="list.laboratories"
          disabled={!workOrderSelected}
          disabledOptions={[laboratoryId as string]}
          queryParams={{ restrictToCurrentUser: false }} //Don't restrict by the user's accessible laboratories
          placeholder="Select or enter Laboratory to search"
          onChange={handleLaboratoryChange}
        />
      </ListFieldSection>
      <ListFieldSection heading="Transfer Note" wide>
        <Field
          input={<TextAreaInput />}
          rows={4}
          maxLength={500}
          value={transferNote}
          onChange={(e: any) => setTransferNote(e.target.value)}
        />
      </ListFieldSection>
      <ListFieldSection
        messages={
          workOrderSelected
            ? messages.map(({ message, hasMessage, severity }) =>
                hasMessage ? { message: message, severity: severity ?? 'info' } : {}
              )
            : []
        }
      >
        <ButtonGroup>
          <ConfirmDialog
            confirmTitle="Confirm Work Order Transfer"
            confirmText={() => (
              <ListFieldSection
                wide
                messages={
                  anySampleHasNonGlobalSpecification
                    ? [
                        {
                          message:
                            'Note: Some samples on this work order have specifications applied which are not global in scope. This selection will not be retained after transfer.',
                          severity: 'warning',
                        },
                      ]
                    : []
                }
              >
                <Message border={false}>
                  {`Transferring this work order to ${laboratoryOption?.label} will make it unavailable in ${laboratory} until it is transferred back. Proceed?`}
                </Message>
              </ListFieldSection>
            )}
            confirmButtonText="Transfer"
            confirmCancelButtonText="Cancel"
          >
            <Button
              primary
              disabled={
                !(
                  laboratorySelected &&
                  transferNoteHasValue &&
                  isClientGlobal &&
                  isProjectGlobal &&
                  !containsUnsupportedSamples
                )
              }
              loading={busyState}
              onClick={transfer}
            >
              Transfer
            </Button>
          </ConfirmDialog>
          <InterLabViewHistoryDialog />
        </ButtonGroup>
      </ListFieldSection>
    </>
  );
};

export { InterLabTransfer };
